import React from 'react'
import { setStorage, getStorage } from 'src/services/localStorage'
import { useAccountSelect } from './useAccount'
import { useNumbersActions, useNumbersSelect } from './useNumbers'
import { useEmailActions, useEmailSelect } from './useEmail'
import { Drivers } from 'src/services/accounts'
import { IConnectedAccount } from './useAccount'
import { isEqual } from 'lodash'

export enum AccountsType {
  EMAIL = 'EMAIL',
  COMMUNICATION = 'COMMUNICATION',
}

/** Backend account + local-only default flag (persisted in localStorage). */
export type IAccountWithDefault = IConnectedAccount & {
  useDefault?: boolean
}

type UseSelectedAccountsOptions = {
  search?: string
}

const getAccountsFromStorage = (
  accountsType: AccountsType
): IAccountWithDefault[] => {
  const storage = getStorage()

  return storage.accountsConfig?.[accountsType] || []
}

const setAccountsToStorage = (
  accountsType: AccountsType,
  accounts: IAccountWithDefault[]
) => {
  const storage = getStorage()
  setStorage({
    accountsConfig: {
      ...storage.accountsConfig,
      [accountsType]: accounts,
    },
  })
}

export const useSelectedAccounts = (
  accountsType: AccountsType,
  options?: UseSelectedAccountsOptions
) => {
  const search = options?.search
  const isSearchConsumer = search !== undefined

  const [accounts, setAccounts] = React.useState<IAccountWithDefault[] | null>(
    null
  )

  const user = useAccountSelect.user()

  const numbers = useNumbersSelect.numbers()
  const getUserNumbers = useNumbersActions.getNumbers()
  const getUserNumbersStatus = useNumbersActions.getNumbers.status()

  const userMailAccounts = useEmailSelect.userMailAccounts()
  const getUserMailAccounts = useEmailActions.getUserEmailsAccounts()
  const getUserMailAccountsStatus =
    useEmailActions.getUserEmailsAccounts.status()

  const isEmailType = accountsType === AccountsType.EMAIL
  const sourceData = (
    isEmailType ? userMailAccounts : numbers.data
  ) as IConnectedAccount[]
  const cursor = isEmailType ? null : numbers.cursor
  const listSearch = isEmailType ? '' : numbers.listSearch ?? ''
  const isListSearchActive = Boolean(listSearch)

  // Загружены ли данные источника (redux). Пока запрос не завершён,
  // пустой sourceData ещё не означает, что аккаунтов нет.
  const isSourceLoaded = isEmailType
    ? !getUserMailAccountsStatus.isPending
    : !getUserNumbersStatus.isPending

  const sourceDataKey = React.useMemo(
    () =>
      JSON.stringify(
        sourceData.map((acc) => ({
          id: acc.id,
          status: acc.status,
          assignedUsers: acc.assignedUsers?.map((user) => user.id),
        }))
      ),
    [sourceData]
  )

  const selectedDefaultAccount = React.useMemo(() => {
    return accounts?.find((acc) => acc.useDefault)
  }, [accounts])

  React.useEffect(() => {
    const storageAccounts = getAccountsFromStorage(accountsType)

    setAccounts(storageAccounts)
  }, [accountsType])

  const searchRef = React.useRef(search)
  const isSearchConsumerRef = React.useRef(isSearchConsumer)
  searchRef.current = search
  isSearchConsumerRef.current = isSearchConsumer

  React.useEffect(() => {
    const handleAccountsSync = (event: CustomEvent) => {
      if (event.detail.accountsType !== accountsType) {
        return
      }

      const storageAccounts = getAccountsFromStorage(accountsType)

      // While the numbers table is showing search results, only sync useDefault
      // flags — do not replace the filtered list with the full storage list.
      if (isSearchConsumerRef.current && searchRef.current) {
        setAccounts((prev) => {
          if (!prev) {
            return prev
          }

          return prev.map((acc) => ({
            ...acc,
            useDefault: storageAccounts.find((s) => s.id === acc.id)
              ?.useDefault,
          }))
        })

        return
      }

      setAccounts((prev) =>
        isEqual(prev, storageAccounts) ? prev : storageAccounts
      )
    }

    window.addEventListener('accounts-sync' as any, handleAccountsSync as any)

    return () => {
      window.removeEventListener(
        'accounts-sync' as any,
        handleAccountsSync as any
      )
    }
  }, [accountsType])

  // Refetch numbers when the page search query changes.
  // initialize() in VerticalLayout loads the full list without search.
  React.useEffect(() => {
    if (isEmailType || !isSearchConsumer || !user?.id) {
      return
    }

    if (search && search.length > 2) {
      getUserNumbers({
        id: user.id,
        search,
      })

      return
    }

    // Restore full list after the user clears search or goes below min length
    if (listSearch) {
      getUserNumbers({
        id: user.id,
      })
    }
  }, [search, isEmailType, isSearchConsumer, user?.id])

  React.useEffect(() => {
    // Пока источник (redux) не догрузился — ничего не синхронизируем,
    // иначе пустой sourceData затрёт локальный стейт и storage.
    if (!isSourceLoaded) {
      return
    }

    // Other hook instances (widgets, layout) must not overwrite default-account
    // storage from a filtered search result set.
    if (!isEmailType && isListSearchActive && !isSearchConsumer) {
      return
    }

    if (sourceData.length > 0 && accounts !== null) {
      // useDefault exists only in localStorage — never on backend account payloads.
      const storageAccounts = getAccountsFromStorage(accountsType)

      let accountsWithDefault = sourceData.map((acc) => ({
        ...acc,
        useDefault: Boolean(
          storageAccounts.find((a) => a.id === acc.id)?.useDefault
        ),
      }))

      const skipAutoDefault = !isEmailType && isListSearchActive

      if (!skipAutoDefault) {
        const currentDefaultAccount = accountsWithDefault.find(
          (acc) => acc.useDefault
        )

        const isCurrentDefaultInvalid = currentDefaultAccount
          ? isEmailType
            ? currentDefaultAccount.status === 'deactivate'
            : currentDefaultAccount.driver !== Drivers.TWILIO ||
              currentDefaultAccount.status === 'inactive'
          : false

        const hasValidDefaultAccount = accountsWithDefault.some(
          (acc) => acc.useDefault && !isCurrentDefaultInvalid
        )

        if (!hasValidDefaultAccount && accountsWithDefault.length > 0) {
          const firstValidAccountIndex = isEmailType
            ? accountsWithDefault.findIndex(
                (acc) => acc.status !== 'deactivate'
              )
            : accountsWithDefault.findIndex(
                (acc) =>
                  acc.driver === Drivers.TWILIO && acc.status !== 'inactive'
              )

          if (firstValidAccountIndex !== -1) {
            accountsWithDefault = accountsWithDefault.map((acc, idx) => ({
              ...acc,
              useDefault: idx === firstValidAccountIndex,
            }))
          } else {
            accountsWithDefault = accountsWithDefault.map((acc) => ({
              ...acc,
              useDefault: false,
            }))
          }
        }
      }

      if (!isEqual(accounts, accountsWithDefault)) {
        setAccounts(accountsWithDefault)
        // Never persist a filtered search list into default-account storage
        if (!skipAutoDefault) {
          setAccountsToStorage(accountsType, accountsWithDefault)
        }
      }
    } else {
      if (isListSearchActive) {
        // Empty search results: clear local list only, keep storage defaults
        if (accounts !== null && accounts.length > 0) {
          setAccounts([])
        }

        return
      }

      if (accounts !== null && accounts.length > 0) {
        setAccounts([])
        setAccountsToStorage(accountsType, [])
      }
    }
  }, [
    sourceDataKey,
    accountsType,
    isEmailType,
    accounts,
    isSourceLoaded,
    isListSearchActive,
    isSearchConsumer,
  ])

  const initialize = () => {
    if (isEmailType) {
      getUserMailAccounts({
        userId: user?.id,
      })
    } else {
      getUserNumbers({
        id: user?.id,
      })
    }
  }

  const setDefaultAccount = React.useCallback(
    (accountId: number) => {
      if (!accounts) return

      const storageAccounts = getAccountsFromStorage(accountsType)

      // Persist defaults against the full localStorage list, not only the
      // currently visible (possibly search-filtered) accounts.
      const baseAccounts =
        storageAccounts.length > 0
          ? storageAccounts.map((stored) => {
              const fromList = accounts.find((a) => a.id === stored.id)

              return fromList ? { ...stored, ...fromList } : stored
            })
          : accounts

      const withCurrent = [...baseAccounts]
      accounts.forEach((acc) => {
        if (!withCurrent.some((a) => a.id === acc.id)) {
          withCurrent.push(acc)
        }
      })

      const updatedStorageAccounts = withCurrent.map((acc) => {
        const isValidAccount = isEmailType
          ? acc.status !== 'deactivate'
          : acc.driver === Drivers.TWILIO && acc.status !== 'inactive'

        return {
          ...acc,
          useDefault: isValidAccount ? acc.id === accountId : false,
        }
      })

      const updatedVisibleAccounts = accounts.map((acc) => {
        const isValidAccount = isEmailType
          ? acc.status !== 'deactivate'
          : acc.driver === Drivers.TWILIO && acc.status !== 'inactive'

        return {
          ...acc,
          useDefault: isValidAccount ? acc.id === accountId : false,
        }
      })

      setAccounts(updatedVisibleAccounts)
      setAccountsToStorage(accountsType, updatedStorageAccounts)

      window.dispatchEvent(
        new CustomEvent('accounts-sync', {
          detail: { accountsType },
        })
      )
    },
    [accounts, accountsType, isEmailType]
  )

  return {
    accounts: accounts || [],
    selectedDefaultAccount,
    initialize,
    cursor,
    setDefaultAccount,
  }
}
